/**
 * Footer Context
 *
 * React context for footer width management.
 * Provides getProjectContainerWidthFn to child components for responsive footer layout.
 *
 * Pattern follows ui-components module contexts:
 * - createContext with null default
 * - Provider with useMemo for context value
 * - Hook with null check and error throw
 *
 * Note: This is a simpler context - no state management, just passes through width function.
 */

import React, { createContext, useContext, useMemo } from 'react';

/**
 * Footer context value interface
 */
interface FooterContextValue {
  getProjectContainerWidthFn: () => number;
}

/**
 * Footer Provider props interface
 */
export interface FooterProviderProps {
  getProjectContainerWidthFn: () => number;
  children: React.ReactNode;
}

/**
 * Footer Context
 */
const FooterContext = createContext<FooterContextValue | null>(null);

/**
 * Footer Provider Component
 *
 * Provides width function to all child components via context.
 */
export function FooterProvider({ getProjectContainerWidthFn, children }: FooterProviderProps) {
  const contextValue = useMemo<FooterContextValue>(() => ({
    getProjectContainerWidthFn
  }), [getProjectContainerWidthFn]);

  return (
    <FooterContext.Provider value={contextValue}>
      {children}
    </FooterContext.Provider>
  );
}

/**
 * Hook to use footer context
 *
 * @throws Error when used outside FooterProvider
 */
export function useFooterContext(): FooterContextValue {
  const context = useContext(FooterContext);

  if (!context) {
    throw new Error('useFooterContext must be used within FooterProvider');
  }

  return context;
}
